/**
 * Calendar-date (date-only) helpers for `targetDate` / `startDate`.
 *
 * These fields are stored as a calendar date but may arrive either bare
 * ("2026-06-03") or as a full ISO instant ("2026-06-03T00:00:00+00:00")
 * because the DB column is DateTime(timezone=True). `new Date("2026-06-03")`
 * parses as UTC midnight, which renders one day early in any behind-UTC zone
 * (A2F-1009). Strip at the 'T' and parse the Y-M-D as LOCAL midnight.
 *
 * Use ONLY for calendar dates (targetDate/startDate). Do NOT use for instant
 * timestamps (completedAt/signedAt/createdAt/dismissedAt) — `new Date(...)` is
 * correct for those.
 */

function dateParts(s: string | null | undefined): [number, number, number] | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.split('T')[0]);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return [y, mo, d];
}

/** Parse a date-only string as LOCAL midnight. Returns null on null/malformed. */
export function parseDateOnly(s: string | null | undefined): Date | null {
  const p = dateParts(s);
  return p ? new Date(p[0], p[1] - 1, p[2]) : null;
}

/**
 * Format a date-only string for display. TZ-safe (parses local midnight).
 * Locale is fixed to 'en-US' to match every existing callsite. Returns ''
 * on null/malformed so guarded callsites render nothing.
 */
export function formatDateOnly(
  s: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' },
): string {
  const d = parseDateOnly(s);
  return d ? d.toLocaleDateString('en-US', opts) : '';
}
